import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from 'react-router-dom';
import { LoadingButton } from "@mui/lab";
import UpdateIcon from "@mui/icons-material/Update";
import { MachineDetails } from "./machineDetails";
import {
  SavingFailedAlert,
  SavingSuccessAlert,
} from "../../components/molecules/SavingModal";
import CustomButton from "../../components/molecules/button";
import { AppDispatch } from "../../state/configureStore";
import { changeMotherBoard } from "../../state/thunks/changeMotherBoardThunks";
import { padding } from "../../theme";

interface IComponentOwnProps {
  open: boolean;
  closeModal: Function;
  details: MachineDetails | undefined;
}

const successMessage =
  "Change mother board command sent successfully.";

function VerifyChangeMotherBoardModal(
  props: IComponentOwnProps,
): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>();
  const [savingStatus, setSavingStatus] = useState<boolean>(false);
  const [savingFailed, setSavingFailed] = useState<boolean | null>(null);
  const [reason, setReason] = useState("");

  const handleClose = () => {
    setSavingFailed(null);
    props.closeModal();
  };

  useEffect(() => {
    if (props.open) {
      setReason("");
      setSavingFailed(null);
    }
  }, [props.open]);

  const onVerify = async () => {
    if (!props.details?.serialNumber) {
      return;
    }
    if (!reason.trim()) {
      return;
    }

    setSavingStatus(true);
    setSavingFailed(null);

    try {
      await dispatch(
        changeMotherBoard({
          serialNumber: props.details.serialNumber,
          deviceIdBeforeChange: props.details.deviceId ?? "",
          reason: reason.trim(),
        }),
      ).unwrap();

      setSavingStatus(false);
      setSavingFailed(false);
    } catch {
      setSavingStatus(false);
      setSavingFailed(true);
    }
  };

  const isSuccess = savingFailed === false;

  return (
    <Dialog open={props.open} onClose={handleClose}>
      <DialogTitle>Change mother board</DialogTitle>

      <DialogContent>
        <SavingFailedAlert
          savingFailedMessage="Change mother board failed! Try again later."
          show={savingFailed === true}
        />
        <SavingSuccessAlert
          savingSuccessMessage={successMessage}
          show={isSuccess}
        />
        {!isSuccess ? (
          <React.Fragment>
            <Typography component="p" variant="body2" sx={{ mb: 2 }}>
              This action registers that the hub&apos;s mother board has been
              replaced. Only continue if you are sure the hardware change has
              been completed. 
              <br /><br />
              To finalise the change, restart the hub and connect serial id and mother board (device id){' '}
              <Link 
              href="https://jolly-island-0d7d01203.2.azurestaticapps.net/"
              target="_blank" 
              rel="noopener"
              >
              here
              </Link>
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              required
              label="Reason for change"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              margin="none"
              sx={{ mt: padding / 2 }}
              disabled={savingStatus}
            />
          </React.Fragment>
        ) : null}
      </DialogContent>

      <DialogActions>
        {isSuccess ? (
          <CustomButton onClick={handleClose}>Close</CustomButton>
        ) : (
          <React.Fragment>
            <CustomButton outlined onClick={handleClose}>
              Cancel
            </CustomButton>
            <LoadingButton
              disabled={savingStatus || !reason.trim()}
              loading={savingStatus}
              onClick={() => {
                void onVerify();
              }}
              loadingPosition="start"
              startIcon={<UpdateIcon />}
              variant="contained"
            >
              Change
            </LoadingButton>
          </React.Fragment>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default VerifyChangeMotherBoardModal;
